import { useEffect, useRef, useState } from 'react';
import type { DirectoryGroup, DirectoryUser } from '../app/state';

/**
 * The subject picker (V0.37.1, extracted V0.38) — chips for what is chosen, a slider between
 * people and groups, and a search over whichever half is showing. THE ONE IMPLEMENTATION:
 * the node popup's responsibles row and the WorkflowControl popover's doc-responsibles row
 * both render this, differing only in which eligibility flag filters the lists and in what
 * the empty states say. A second copy of a picker is the failure mode this codebase has
 * already paid for once.
 *
 * FILTERED TO ELIGIBLE ACCOUNTS/GROUPS by the caller's predicates: the server refuses an
 * ineligible assignee by name, so offering one would be drawing a 400. An already-chosen id
 * is kept even if the directory cannot currently vouch for it, or saving would silently drop
 * an assignment nobody touched.
 *
 * DIRECT rows only, always. Inherited responsibles (people or groups) are an ancestor's to
 * revoke, so the caller renders those as its own read-only rows below — they are never
 * removable chips here.
 *
 * A chosen person who is ALSO covered by a chosen group is harmless, not an error — the
 * server unions them — so this list draws no distinction and needs no cross-reference between
 * the two halves. That is deliberate: the Users half names people and nothing else, so an
 * assignment always reads as WHO it is, never as a computed consequence of team membership.
 *
 * THE LIST IS A DROPDOWN, not a permanently open panel: it appears when the selection box is
 * clicked and closes on Escape or a click outside. Both hosts are small — the node popup's
 * body and the doc bar's popover — and a list that is always open pushes everything below it
 * down (in the popup, out of view) to offer choices nobody has asked for yet. Open, it
 * OVERLAYS what follows rather than reflowing it, so nothing on either surface moves.
 * Choosing does NOT close it: this picks several people, and a dropdown that shuts after
 * every tick makes assigning three of them three journeys.
 *
 * THE CALLER OWNS THE DIRECTORY-TRUST GUARD. This component draws whatever lists it is given;
 * deciding that a draft seeded before the directories loaded must not be trusted (the
 * `respSeedTrusted` rule) is the caller's, because the caller owns the draft and the save.
 */
export interface SubjectPickerProps {
  /** The wrapper's DOM id — `pp-responsibles` in the popup, `wf-responsibles` in the doc bar. */
  id?: string;
  users: readonly DirectoryUser[];
  groups: readonly DirectoryGroup[];
  /** May this account be offered? (`canBeNodeResponsible` / `canBeDocResponsible`.) */
  eligibleUser: (u: DirectoryUser) => boolean;
  /** May this group be offered? (Same two flags, group-shaped.) */
  eligibleGroup: (g: DirectoryGroup) => boolean;
  chosenUsers: string[];
  chosenGroups: string[];
  onChangeUsers: (next: string[]) => void;
  onChangeGroups: (next: string[]) => void;
  /** What the Users half says when nobody is eligible (names the missing brick). */
  emptyUsersText: string;
  /** The Groups half's same sentence. */
  emptyGroupsText: string;
  /** Start with the options list already open and the input focused (V0.42) — for the doc
   *  bar's responsibles popover, where the box's own click already opened the surface, so a
   *  second click just to reveal the list is a click too many. */
  autoOpen?: boolean;
}

export function SubjectPicker({
  id,
  users,
  groups,
  eligibleUser,
  eligibleGroup,
  chosenUsers,
  chosenGroups,
  onChangeUsers,
  onChangeGroups,
  emptyUsersText,
  emptyGroupsText,
  autoOpen,
}: SubjectPickerProps) {
  const [mode, setMode] = useState<'users' | 'groups'>('users');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(!!autoOpen);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /* A click anywhere outside closes it. On `pointerdown` rather than `click` so a press that
     lands on something which moves under the cursor still closes this first, and in the
     CAPTURE phase so a handler that stops propagation cannot leave the list stranded open. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open]);

  /* autoOpen: the list starts open, so focus the input once mounted — the caller's own click
     already opened the surface, and a second click just to reveal the list is one too many. */
  useEffect(() => {
    if (autoOpen) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Both hosts scroll (the popup's body, the doc bar's popover), and an absolutely positioned
     list opening near the bottom of one would be half outside it. `block: 'nearest'` scrolls
     only when it actually is, and moves nothing when the list already fits. */
  useEffect(() => {
    if (open) menuRef.current?.scrollIntoView({ block: 'nearest' });
  }, [open]);

  const q = query.trim().toLowerCase();
  const toggleUser = (uid: string) =>
    onChangeUsers(chosenUsers.includes(uid) ? chosenUsers.filter((c) => c !== uid) : [...chosenUsers, uid]);
  const toggleGroup = (gid: string) =>
    onChangeGroups(chosenGroups.includes(gid) ? chosenGroups.filter((c) => c !== gid) : [...chosenGroups, gid]);

  const userRows = users
    .filter((u) => eligibleUser(u) || chosenUsers.includes(u.id))
    .filter((u) => !q || u.displayName.toLowerCase().includes(q));
  const groupRows = groups
    .filter((g) => eligibleGroup(g) || chosenGroups.includes(g.id))
    .filter((g) => !q || g.name.toLowerCase().includes(q));

  return (
    <div
      id={id}
      className="subj-picker"
      ref={rootRef}
      /* Escape belongs to the list while the list is open — the popup and the doc bar both
         close on it, and closing the whole surface because somebody dismissed a dropdown
         throws away their unsaved edits. React attaches at its root container, inside
         `document`, so stopping propagation here is enough to keep chrome.ts from seeing it. */
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || !open) return;
        e.stopPropagation();
        setOpen(false);
        inputRef.current?.focus();
      }}
    >
      <div
        className="subj-box"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={id ? `${id}-menu` : undefined}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {chosenGroups.map((gid) => {
          const g = groups.find((x) => x.id === gid);
          return (
            <span className="subj-tok grp" key={'g:' + gid}>
              <span className="subj-av grp">{initialsOf(g?.name ?? gid)}</span>
              {g?.name ?? gid}
              <button
                type="button"
                className="x"
                aria-label={`Remove ${g?.name ?? gid}`}
                onClick={(e) => {
                  e.stopPropagation(); // removing a chip is not a request to browse the list
                  toggleGroup(gid);
                }}
              >
                ×
              </button>
            </span>
          );
        })}
        {chosenUsers.map((uid) => {
          const u = users.find((x) => x.id === uid);
          return (
            <span className="subj-tok" key={'u:' + uid}>
              <span className="subj-av">{initialsOf(u?.displayName ?? uid)}</span>
              {u?.displayName ?? uid}
              <button
                type="button"
                className="x"
                aria-label={`Remove ${u?.displayName ?? uid}`}
                onClick={(e) => {
                  e.stopPropagation(); // as above
                  toggleUser(uid);
                }}
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          className="subj-input"
          type="text"
          placeholder={open ? 'Search people or groups…' : 'Click to assign…'}
          value={query}
          /* Deliberately no `onFocus` opener. Escape closes the list and puts the caret back
             in the box; if focus alone opened it, that focus would reopen what Escape just
             dismissed and the key would appear dead. Clicking the box opens it, and so does
             typing — which is the next thing anyone arriving here by Tab does. */
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true); // typing is a search, and a search with its results hidden is not one
          }}
        />
      </div>

      {open ? (
      <div className="subj-menu" id={id ? `${id}-menu` : undefined} ref={menuRef} role="listbox">
        <div
          className="segmented seg-slide hue"
          data-mode={mode}
          role="radiogroup"
          aria-label="Assign to"
        >
          <span className="seg-thumb" aria-hidden="true" />
          <button
            type="button"
            className={mode === 'users' ? 'seg on' : 'seg'}
            role="radio"
            aria-checked={mode === 'users'}
            onClick={() => setMode('users')}
          >
            Users
          </button>
          <button
            type="button"
            className={mode === 'groups' ? 'seg on' : 'seg'}
            role="radio"
            aria-checked={mode === 'groups'}
            onClick={() => setMode('groups')}
          >
            Groups
          </button>
        </div>

        <div className="subj-list">
          {mode === 'users' ? (
            userRows.length ? (
              userRows.map((u) => (
                <button
                  type="button"
                  key={u.id}
                  className={chosenUsers.includes(u.id) ? 'subj-row sel' : 'subj-row'}
                  onClick={() => toggleUser(u.id)}
                >
                  <span className="subj-av">{initialsOf(u.displayName)}</span>
                  <span className="subj-who">
                    <span className="subj-nm">{u.displayName}</span>
                  </span>
                  <span className="subj-tick">{chosenUsers.includes(u.id) ? '✓' : ''}</span>
                </button>
              ))
            ) : (
              <div className="subj-empty">
                {q ? `No person matches "${query.trim()}".` : emptyUsersText}
              </div>
            )
          ) : groupRows.length ? (
            groupRows.map((g) => (
              <button
                type="button"
                key={g.id}
                className={chosenGroups.includes(g.id) ? 'subj-row sel' : 'subj-row'}
                onClick={() => toggleGroup(g.id)}
              >
                <span className="subj-av grp">{initialsOf(g.name)}</span>
                <span className="subj-who">
                  <span className="subj-nm">{g.name}</span>
                </span>
                <span className="subj-count">{g.memberIds.length}</span>
                <span className="subj-tick">{chosenGroups.includes(g.id) ? '✓' : ''}</span>
              </button>
            ))
          ) : (
            <div className="subj-empty">
              {q ? `No group matches "${query.trim()}".` : emptyGroupsText}
            </div>
          )}
        </div>
      </div>
      ) : null}
    </div>
  );
}

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}
